/**
 * Client chat state (ADR 0015): one live stream per tab (EventSource /v1/me/live — the browser
 * resends Last-Event-ID on reconnect, so missed messages are replayed), the conversation list of
 * the current workspace, loaded message pages, typing indicators, unread counts (+ the app icon
 * badge) and the "what this tab is looking at" focus that suppresses pushes for an open chat.
 *
 * Screens read it through useChat() (useSyncExternalStore) and never talk to EventSource.
 */
import { useSyncExternalStore } from 'react';
import { describeError } from '../api/client.js';
import { newId } from '../lib/ids.js';
import { chatApi } from './api.js';
import type { ActivityItem, Conversation, Message, SendBody } from './types.js';
import { invalidateApprovals } from '../app/approvals.js';

export interface Thread {
  items: Message[];
  hasMore: boolean;
  loading: boolean;
  loaded: boolean;
  error: string | null;
}

type Listener = () => void;
type LiveHandler = (event: string, data: any) => void;

export class ChatStore {
  version = 0;
  me: string | null = null;
  myName = '';
  workspaceId: string | null = null;
  conversations = new Map<string, Conversation>();
  listLoaded = false;
  listError: string | null = null;
  threads = new Map<string, Thread>();
  typing = new Map<string, Map<string, { name: string; until: number }>>();
  /** Unread กิจกรรม items — the one count behind the header bell, the nav entry and the phone tab. */
  activityUnread = 0;
  /** Bumped whenever the feed changes anywhere (new item, read here / in another tab): lists reload. */
  activityEpoch = 0;
  /** Latest activity items pushed live (the Activity page prepends them). */
  liveActivity: ActivityItem[] = [];
  connected = false;
  connId: string | null = null;
  /** Latest known status per person (live `status` events). */
  statuses = new Map<string, Conversation['members'][number]['status']>();
  /** Polite screen-reader announcement for a new message in the open chat. */
  announcement = '';
  private focus: string | null = null;
  private es: EventSource | null = null;
  private listeners = new Set<Listener>();
  private liveHandlers = new Set<LiveHandler>();
  private typingTimer: ReturnType<typeof setInterval> | null = null;
  private lastTypingSent = 0;
  private boundVisibility = false;

  subscribe = (fn: Listener) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getVersion = () => this.version;

  private emit() {
    this.version++;
    for (const fn of this.listeners) fn();
    this.updateBadge();
  }

  /** Call signalling and other screens listen to raw live events. */
  onLive(fn: LiveHandler): () => void {
    this.liveHandlers.add(fn);
    return () => this.liveHandlers.delete(fn);
  }

  // ---------- lifecycle ----------

  start(user: { id: string; displayName: string }, workspaceId: string) {
    const switched = this.workspaceId !== workspaceId || this.me !== user.id;
    this.me = user.id;
    this.myName = user.displayName;
    if (switched) {
      this.workspaceId = workspaceId;
      this.conversations.clear();
      this.threads.clear();
      this.listLoaded = false;
      this.activityUnread = 0;
      this.liveActivity = [];
      void this.refreshList();
      void this.refreshUnread();
    }
    this.connect();
    this.bindVisibility();
    this.typingTimer ??= setInterval(() => this.expireTyping(), 1000);
  }

  stop() {
    this.es?.close();
    this.es = null;
    this.connected = false;
    this.connId = null;
    this.workspaceId = null;
    this.me = null;
    this.conversations.clear();
    this.threads.clear();
    if (this.typingTimer) clearInterval(this.typingTimer);
    this.typingTimer = null;
    this.emit();
  }

  private connect() {
    if (this.es || typeof EventSource === 'undefined') return;
    const es = new EventSource('/v1/me/live');
    this.es = es;
    const on = (name: string, fn: (data: any) => void) => es.addEventListener(name, (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data);
        fn(data);
        for (const h of this.liveHandlers) h(name, data);
      } catch {
        /* malformed event */
      }
    });
    on('hello', (d) => {
      this.connected = true;
      this.connId = d.connId;
      void this.sendFocus();
      // Reconnected without a replay: lists may be stale.
      if (!d.resumed && this.listLoaded) void this.refreshList();
      this.emit();
    });
    on('reset', () => {
      void this.refreshList();
      for (const id of this.threads.keys()) void this.loadLatest(id);
    });
    on('message', (d) => this.onMessage(d.message as Message, true));
    on('message.updated', (d) => this.onMessage(d.message as Message, false));
    on('reaction', (d) => this.onMessage(d.message as Message, false));
    on('read', (d) => this.onRead(d));
    on('typing', (d) => this.onTyping(d));
    on('conversation', (d) => {
      if (d.workspaceId !== this.workspaceId) return;
      if (d.change === 'archived') {
        this.conversations.delete(d.conversationId);
        this.emit();
      } else void this.refreshConversation(d.conversationId);
    });
    on('notification', (d) => {
      if (d.workspaceId !== this.workspaceId) return;
      this.activityUnread++;
      this.activityEpoch++;
      this.liveActivity = [d as ActivityItem, ...this.liveActivity].slice(0, 50);
      // An approval was requested / decided / went stale: chips, the inbox and the nav badge reload.
      const kind = (d as { kind?: unknown }).kind;
      if (typeof kind === 'string' && (kind.startsWith('review.') || kind.startsWith('approval.'))) invalidateApprovals();
      this.emit();
    });
    on('notification.read', (d) => {
      if (d.workspaceId !== this.workspaceId) return;
      this.activityEpoch++;
      void this.refreshUnread();
    });
    on('status', (d: { userId: string; status: Conversation['members'][number]['status']; online: boolean }) => {
      this.statuses.set(d.userId, d.status);
      for (const [id, c] of this.conversations) {
        if (!c.members.some((m) => m.userId === d.userId)) continue;
        this.conversations.set(id, { ...c, members: c.members.map((m) => (m.userId === d.userId ? { ...m, status: d.status, online: d.online } : m)) });
      }
      this.emit();
    });
    // Events other modules handle through onLive() (calls, scheduled sends).
    for (const name of ['call.ring', 'call.signal', 'call.joined', 'call.left', 'call.declined', 'call.ended', 'scheduled']) on(name, () => undefined);
    on('closed', (d) => {
      if (d.reason === 'auth') {
        es.close();
        this.es = null;
      }
    });
    es.onerror = () => {
      this.connected = false;
      this.emit();
      // The browser retries by itself; a hard failure (CLOSED) is retried here.
      if (es.readyState === EventSource.CLOSED) {
        this.es = null;
        setTimeout(() => this.me && this.connect(), 5000);
      }
    };
  }

  private bindVisibility() {
    if (this.boundVisibility || typeof document === 'undefined') return;
    this.boundVisibility = true;
    document.addEventListener('visibilitychange', () => {
      void this.sendFocus();
      if (document.visibilityState === 'visible' && this.focus) this.markRead(this.focus);
    });
  }

  private async sendFocus() {
    // Only on a stream the server has registered: while EventSource reconnects (after onerror,
    // before the next 'hello') the old connId is gone on the server and the POST would be a 409
    // LIVE_NOT_CONNECTED. The desired focus is kept; 'hello' sends it for the new connection.
    if (!this.connId || !this.connected) return;
    const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
    await chatApi.focus(this.connId, visible ? this.focus : null).catch(() => undefined);
  }

  setFocus(conversationId: string | null) {
    if (this.focus === conversationId) return;
    this.focus = conversationId;
    void this.sendFocus();
  }

  get focused() {
    return this.focus;
  }

  // ---------- lists ----------

  async refreshList() {
    const w = this.workspaceId;
    if (!w) return;
    try {
      const { items } = await chatApi.list(w);
      if (w !== this.workspaceId) return;
      // Channels you are not in keep their entry when you look at them.
      const keep = [...this.conversations.values()].filter((c) => !c.joined);
      this.conversations = new Map([...keep, ...items].map((c) => [c.id, c]));
      this.listLoaded = true;
      this.listError = null;
    } catch (e) {
      this.listError = describeError(e);
    }
    this.emit();
  }

  /** Optimistic "read" from the bell or the กิจกรรม page (the server event then confirms the count). */
  markActivityRead(n: number | 'all') {
    this.activityUnread = n === 'all' ? 0 : Math.max(0, this.activityUnread - n);
    this.emit();
  }

  async refreshUnread() {
    const w = this.workspaceId;
    if (!w) return;
    try {
      const u = await chatApi.unread(w);
      if (w === this.workspaceId) this.activityUnread = u.activity;
      this.emit();
    } catch {
      /* next event refreshes */
    }
  }

  async refreshConversation(id: string) {
    try {
      const c = await chatApi.get(id);
      if (c.workspaceId !== this.workspaceId) return;
      this.conversations.set(id, c);
      this.emit();
      return c;
    } catch {
      if (this.conversations.has(id)) {
        this.conversations.delete(id);
        this.emit();
      }
      return null;
    }
  }

  upsertConversation(c: Conversation) {
    this.conversations.set(c.id, c);
    this.emit();
  }

  sorted(): Conversation[] {
    return [...this.conversations.values()]
      .filter((c) => c.joined)
      .sort((a, b) => cmp(b.lastMessageAt ?? b.createdAt, a.lastMessageAt ?? a.createdAt));
  }

  get unreadChats(): number {
    return [...this.conversations.values()].filter((c) => c.joined && c.unread > 0 && !isMuted(c)).length;
  }

  private updateBadge() {
    const nav = typeof navigator !== 'undefined' ? (navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> }) : null;
    if (!nav?.setAppBadge) return;
    const n = this.unreadChats + this.activityUnread;
    void (n > 0 ? nav.setAppBadge(n) : nav.clearAppBadge?.())?.catch?.(() => undefined);
  }

  // ---------- messages ----------

  thread(id: string): Thread {
    return this.threads.get(id) ?? { items: [], hasMore: false, loading: false, loaded: false, error: null };
  }

  private setThread(id: string, t: Partial<Thread>) {
    this.threads.set(id, { ...this.thread(id), ...t });
    this.emit();
  }

  async loadLatest(id: string) {
    this.setThread(id, { loading: true, error: null });
    try {
      const r = await chatApi.messages(id, { limit: 50 });
      const pending = this.thread(id).items.filter((m) => m.local);
      this.setThread(id, { items: merge(r.items, pending), hasMore: r.hasMore, loading: false, loaded: true });
    } catch (e) {
      this.setThread(id, { loading: false, error: describeError(e) });
    }
  }

  async loadOlder(id: string) {
    const t = this.thread(id);
    const first = t.items.find((m) => !m.local);
    if (!t.hasMore || t.loading || !first) return;
    this.setThread(id, { loading: true });
    try {
      const r = await chatApi.messages(id, { before: first.seq, limit: 50 });
      this.setThread(id, { items: merge([...r.items, ...this.thread(id).items], []), hasMore: r.hasMore, loading: false });
    } catch (e) {
      this.setThread(id, { loading: false, error: describeError(e) });
    }
  }

  private onMessage(m: Message, isNew: boolean) {
    if (m.workspaceId !== this.workspaceId) return;
    const t = this.threads.get(m.conversationId);
    if (t?.loaded) {
      // Broadcast copies carry no per-person fields: keep this person's star and poll votes.
      const prev = t.items.find((x) => x.id === m.id);
      if (prev) {
        if (m.starred === undefined && prev.starred !== undefined) m = { ...m, starred: prev.starred };
        if (m.poll && !m.poll.myVotes && prev.poll?.myVotes) m = { ...m, poll: { ...m.poll, myVotes: prev.poll.myVotes } };
      }
      this.threads.set(m.conversationId, { ...t, items: merge([...t.items, m], []) });
    }
    const c = this.conversations.get(m.conversationId);
    const mine = m.authorId === this.me;
    const viewing = this.focus === m.conversationId && typeof document !== 'undefined' && document.visibilityState === 'visible';
    if (c) {
      const updated: Conversation = { ...c };
      if (isNew && m.seq > c.lastSeq) {
        updated.lastSeq = m.seq;
        updated.lastMessageAt = m.createdAt;
        updated.lastMessage = { id: m.id, preview: previewOf(m), authorId: m.authorId, authorName: m.authorName, kind: m.kind, deleted: !!m.deletedAt, createdAt: m.createdAt };
        if (!mine && !viewing && m.kind !== 'system') {
          updated.unread = c.unread + 1;
          if (m.mentions.includes(this.me ?? '') || m.mentionAll) updated.mentionUnread = true;
        }
        if (mine || viewing) updated.lastReadSeq = Math.max(c.lastReadSeq, m.seq);
      } else if (c.lastMessage?.id === m.id) {
        updated.lastMessage = { ...c.lastMessage, preview: previewOf(m), deleted: !!m.deletedAt };
      }
      this.conversations.set(c.id, updated);
    } else if (isNew) {
      void this.refreshConversation(m.conversationId);
    }
    if (isNew && viewing && !mine) {
      this.announcement = `${m.authorName ?? 'ระบบ'}: ${previewOf(m)}`;
      this.markRead(m.conversationId, m.seq);
    }
    // The typing indicator of the sender ends with their message.
    if (isNew && m.authorId) this.typing.get(m.conversationId)?.delete(m.authorId);
    this.emit();
  }

  private onRead(d: { workspaceId: string; conversationId: string; userId: string; seq: number; unread: boolean }) {
    const c = this.conversations.get(d.conversationId);
    if (!c) return;
    const members = c.members.map((m) => (m.userId === d.userId ? { ...m, lastReadSeq: d.unread ? d.seq : Math.max(m.lastReadSeq, d.seq) } : m));
    const updated: Conversation = { ...c, members };
    if (d.userId === this.me) {
      updated.lastReadSeq = d.seq;
      const t = this.threads.get(c.id);
      updated.unread = t?.loaded ? t.items.filter((m) => m.seq > d.seq && m.authorId !== this.me && !m.deletedAt && m.kind !== 'system' && !m.local).length : d.seq >= c.lastSeq ? 0 : c.unread;
      if (d.seq >= c.lastSeq) updated.mentionUnread = false;
    }
    this.conversations.set(c.id, updated);
    this.emit();
  }

  private onTyping(d: { workspaceId: string; conversationId: string; userId: string; name: string }) {
    if (d.userId === this.me) return;
    const m = this.typing.get(d.conversationId) ?? new Map();
    m.set(d.userId, { name: d.name, until: Date.now() + 6000 });
    this.typing.set(d.conversationId, m);
    this.emit();
  }

  private expireTyping() {
    let changed = false;
    const now = Date.now();
    for (const m of this.typing.values()) for (const [u, t] of m) if (t.until < now) {
      m.delete(u);
      changed = true;
    }
    if (changed) this.emit();
  }

  typingNames(conversationId: string): string[] {
    return [...(this.typing.get(conversationId)?.values() ?? [])].map((t) => t.name);
  }

  notifyTyping(conversationId: string) {
    const now = Date.now();
    if (now - this.lastTypingSent < 3000) return;
    this.lastTypingSent = now;
    void chatApi.typing(conversationId).catch(() => undefined);
  }

  markRead(conversationId: string, seq?: number) {
    const c = this.conversations.get(conversationId);
    const target = seq ?? c?.lastSeq ?? this.thread(conversationId).items.at(-1)?.seq ?? 0;
    if (!c || !c.joined) return;
    if (target <= c.lastReadSeq && c.unread === 0) return;
    this.conversations.set(conversationId, { ...c, unread: 0, mentionUnread: false, lastReadSeq: Math.max(c.lastReadSeq, target) });
    this.emit();
    void chatApi.read(conversationId, target).then(() => this.refreshUnread()).catch(() => undefined);
  }

  /** Optimistic send: the bubble shows at once ("กำลังส่ง…"), is replaced by the server's, or offers retry. */
  async send(conversationId: string, body: Omit<SendBody, 'clientId'>, preview?: Partial<Message>): Promise<Message | null> {
    const clientId = `c-${newId()}`;
    const local: Message = {
      id: clientId, conversationId, workspaceId: this.workspaceId ?? '', seq: Number.MAX_SAFE_INTEGER, authorId: this.me, authorName: this.myName,
      kind: body.kind ?? 'text', body: body.body ?? '', plain: body.body ?? '', mentions: [], mentionAll: null, attachments: [], card: null, poll: null,
      replyTo: null, replyCount: 0, reactions: [], linkPreview: null, forwarded: false, clientId, editedAt: null, deletedAt: null, pinnedAt: null, pinnedBy: null,
      createdAt: new Date().toISOString(), local: 'sending', ...preview,
    };
    const full: SendBody = { ...body, clientId };
    this.pending.set(clientId, full);
    const t = this.thread(conversationId);
    this.setThread(conversationId, { items: [...t.items, local] });
    return this.deliver(conversationId, clientId);
  }

  private pending = new Map<string, SendBody>();

  async retry(conversationId: string, clientId: string) {
    const t = this.thread(conversationId);
    this.setThread(conversationId, { items: t.items.map((m) => (m.clientId === clientId ? { ...m, local: 'sending' } : m)) });
    return this.deliver(conversationId, clientId);
  }

  discard(conversationId: string, clientId: string) {
    this.pending.delete(clientId);
    const t = this.thread(conversationId);
    this.setThread(conversationId, { items: t.items.filter((m) => m.clientId !== clientId || !m.local) });
  }

  private async deliver(conversationId: string, clientId: string): Promise<Message | null> {
    const body = this.pending.get(clientId);
    if (!body) return null;
    try {
      const r = await chatApi.send(conversationId, body);
      this.pending.delete(clientId);
      if ('scheduled' in r) {
        this.discard(conversationId, clientId);
        return null;
      }
      this.onMessage(r, true);
      return r;
    } catch (e) {
      const t = this.thread(conversationId);
      this.setThread(conversationId, { items: t.items.map((m) => (m.clientId === clientId && m.local ? { ...m, local: 'failed' } : m)) });
      throw e;
    }
  }

  /** Replace one message with the server's answer (reactions, votes, edits). */
  apply(m: Message) {
    this.onMessage(m, false);
  }
}

/** Plain code-unit order (ISO timestamps); never locale collation. */
const cmp = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

export function isMuted(c: Conversation): boolean {
  return !!c.mutedUntil && new Date(c.mutedUntil).getTime() > Date.now();
}

export function previewOf(m: Message): string {
  if (m.deletedAt) return 'ลบข้อความแล้ว';
  if (m.kind === 'voice') return '🎤 ข้อความเสียง';
  if (m.kind === 'poll') return `📊 ${m.plain}`;
  if (m.kind === 'call') return `📞 ${m.plain}`;
  if (m.card && !m.plain) return `แชร์ ${m.card.title}`;
  if (!m.plain && m.attachments.length) return m.attachments.every((a) => a.kind === 'image') ? '🖼️ รูปภาพ' : '📎 ไฟล์แนบ';
  return m.plain.replace(/\*\*|~~|`+/g, '').replace(/\s+/g, ' ').slice(0, 90);
}

/** Merge by id (server copies replace optimistic ones by clientId), ordered by seq. */
function merge(a: Message[], b: Message[]): Message[] {
  const byId = new Map<string, Message>();
  for (const m of [...a, ...b]) {
    if (!m.local && m.clientId) {
      for (const [k, v] of byId) if (v.local && v.clientId === m.clientId) byId.delete(k);
    }
    if (m.local && [...byId.values()].some((x) => !x.local && x.clientId === m.clientId)) continue;
    byId.set(m.id, m);
  }
  return [...byId.values()].sort((x, y) => x.seq - y.seq || cmp(x.createdAt, y.createdAt));
}

export const chatStore = new ChatStore();

export function useChat(): ChatStore {
  useSyncExternalStore(chatStore.subscribe, chatStore.getVersion, chatStore.getVersion);
  return chatStore;
}
